import {
  ArrowRight,
  Calendar,
  CheckCircle2,
  Clock,
  Download,
  Eye,
  Mail,
  Phone,
  Search,
  Star,
  ThumbsDown,
  ThumbsUp,
  Trophy,
  UserRound,
  Users,
  X,
  XCircle,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { SERVER_URL } from '../../api/jobBoardApi';
import Spinner from '../../components/Spinner';
import {
  useAllApplicants,
  useUpdateApplicantStatusAny,
} from '../../hooks/useJobBoard';
import { ApplicantDto, ApplicationStatus } from '../../types/jobBoard';

const STATUS_ORDER: ApplicationStatus[] = [
  'New',
  'Reviewed',
  'Shortlisted',
  'Rejected',
  'Hired',
];

const STATUS_BADGE: Record<ApplicationStatus, string> = {
  New: 'bg-slate-100 text-slate-700',
  Reviewed: 'bg-blue-50 text-blue-700',
  Shortlisted: 'bg-violet-50 text-violet-700',
  Rejected: 'bg-red-50 text-red-700',
  Hired: 'bg-emerald-50 text-emerald-700',
};

const STATUS_DOT: Record<ApplicationStatus, string> = {
  New: 'bg-slate-500',
  Reviewed: 'bg-blue-600',
  Shortlisted: 'bg-violet-600',
  Rejected: 'bg-red-600',
  Hired: 'bg-emerald-600',
};

const PIPELINE_STAGES: ApplicationStatus[] = [
  'New',
  'Reviewed',
  'Shortlisted',
  'Hired',
];

const label = (app: ApplicantDto) =>
  app.fullName ??
  ([app.firstName, app.lastName].filter(Boolean).join(' ') || 'Applicant');

const appliedOn = (app: ApplicantDto) => app.applicationDate ?? app.appliedDate;

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString() : '-';

const formatDateTime = (value?: string) =>
  value
    ? new Date(value).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '-';

const resumeHref = (app: ApplicantDto) => {
  const resume = app.resumeUrl ?? app.resumePath;
  if (!resume) return undefined;
  return resume.startsWith('http') ? resume : `${SERVER_URL}/${resume}`;
};

const toCsv = (rows: ApplicantDto[], jobTitle: (jobId: string) => string) => {
  const header = [
    'Name',
    'Email',
    'Contact Number',
    'Position',
    'Applied Date',
    'Status',
  ];
  const lines = rows.map((app) =>
    [
      label(app),
      app.emailAddress,
      app.contactNumber ?? '',
      jobTitle(app.jobId),
      appliedOn(app) ? formatDate(appliedOn(app)) : '',
      app.applicationStatus,
    ]
      .map((value) => `"${String(value).replace(/"/g, '""')}"`)
      .join(',')
  );
  return [header.join(','), ...lines].join('\n');
};

const downloadCsv = (csv: string, filename: string) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

interface Filters {
  search: string;
  status: 'All' | ApplicationStatus;
  jobId: string;
  from: string;
  to: string;
}

const EMPTY_FILTERS: Filters = {
  search: '',
  status: 'All',
  jobId: 'All',
  from: '',
  to: '',
};

const ApplicantsOverview = () => {
  const { data: applicants, isLoading, jobs } = useAllApplicants();
  const update = useUpdateApplicantStatusAny();

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [activeApplicant, setActiveApplicant] = useState<ApplicantDto | null>(
    null
  );

  const jobTitle = useMemo(() => {
    const map = new Map(jobs.map((job) => [job.jobId, job.title]));
    return (jobId: string) => map.get(jobId) ?? 'Job';
  }, [jobs]);

  const filtersActive =
    filters.search !== '' ||
    filters.status !== 'All' ||
    filters.jobId !== 'All' ||
    filters.from !== '' ||
    filters.to !== '';

  const filtered = useMemo(() => {
    const term = filters.search.trim().toLowerCase();

    return applicants.filter((app) => {
      const matchesSearch =
        !term ||
        label(app).toLowerCase().includes(term) ||
        app.emailAddress.toLowerCase().includes(term) ||
        (app.contactNumber ?? '').toLowerCase().includes(term);

      const matchesStatus =
        filters.status === 'All' || app.applicationStatus === filters.status;

      const matchesJob = filters.jobId === 'All' || app.jobId === filters.jobId;

      const applied = appliedOn(app);
      const appliedTime = applied ? new Date(applied).getTime() : undefined;

      const matchesFrom =
        !filters.from ||
        (appliedTime !== undefined &&
          appliedTime >= new Date(filters.from).getTime());

      const matchesTo =
        !filters.to ||
        (appliedTime !== undefined &&
          appliedTime <= new Date(filters.to).getTime() + 86400000 - 1);

      return matchesSearch && matchesStatus && matchesJob && matchesFrom && matchesTo;
    });
  }, [applicants, filters]);

  const counts = useMemo(() => {
    const base: Record<ApplicationStatus, number> = {
      New: 0,
      Reviewed: 0,
      Shortlisted: 0,
      Rejected: 0,
      Hired: 0,
    };
    applicants.forEach((app) => {
      base[app.applicationStatus] = (base[app.applicationStatus] ?? 0) + 1;
    });
    return base;
  }, [applicants]);

  const total = applicants.length;

  const recentActivity = useMemo(
    () =>
      [...applicants]
        .sort((a, b) => {
          const dateA = appliedOn(a) ? new Date(appliedOn(a) as string).getTime() : 0;
          const dateB = appliedOn(b) ? new Date(appliedOn(b) as string).getTime() : 0;
          return dateB - dateA;
        })
        .slice(0, 8),
    [applicants]
  );

  const toggleSelected = (id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((app) => app.applicantId)));
    }
  };

  const clearFilters = () => setFilters(EMPTY_FILTERS);

  const filterByStage = (stage: ApplicationStatus) =>
    setFilters((current) => ({
      ...current,
      status: current.status === stage ? 'All' : stage,
    }));

  const setStatus = (
    applicantId: string,
    jobId: string,
    status: ApplicationStatus
  ) => {
    update.mutate({ applicantId, jobId, status });
  };

  const bulkSetStatus = (status: ApplicationStatus) => {
    if (!selected.size) return;
    if (
      !window.confirm(
        `Move ${selected.size} applicant${selected.size === 1 ? '' : 's'} to ${status}?`
      )
    )
      return;

    applicants
      .filter((app) => selected.has(app.applicantId))
      .forEach((app) =>
        update.mutate({ applicantId: app.applicantId, jobId: app.jobId, status })
      );
    setSelected(new Set());
  };

  const exportRows = (rows: ApplicantDto[], filename: string) => {
    if (!rows.length) return;
    downloadCsv(toCsv(rows, jobTitle), filename);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-12">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6 text-left">
      {/* Header */}
      <header className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-strivre-navy">
            <Users size={22} className="text-strivre-burgundy" />
            Applicant Management
          </h1>

          <p className="mt-1 text-sm text-strivre-mutedtext">
            Manage applications, track candidate progress, and make hiring decisions.
          </p>
        </div>

        <span className="inline-flex items-center gap-2 self-start rounded-full border border-strivre-lightnavy bg-white px-4 py-2 text-sm font-semibold text-strivre-navy sm:self-auto">
          <Users size={15} className="text-strivre-burgundy" />
          {total} total applicant{total === 1 ? '' : 's'}
        </span>
      </header>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
        <div className="rounded-2xl border border-strivre-lightnavy bg-white p-5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Total
            </p>
            <Users size={18} className="text-strivre-burgundy" />
          </div>
          <p className="mt-2 text-3xl font-bold text-strivre-navy">{total}</p>
          <p className="mt-1 text-xs text-slate-400">All applicants</p>
        </div>

        <div className="rounded-2xl border border-strivre-lightnavy bg-white p-5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              New
            </p>
            <Clock size={18} className="text-slate-500" />
          </div>
          <p className="mt-2 text-3xl font-bold text-slate-700">{counts.New}</p>
          <p className="mt-1 text-xs text-slate-400">Awaiting review</p>
        </div>

        <div className="rounded-2xl border border-strivre-lightnavy bg-white p-5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Reviewed
            </p>
            <Eye size={18} className="text-blue-600" />
          </div>
          <p className="mt-2 text-3xl font-bold text-blue-600">{counts.Reviewed}</p>
          <p className="mt-1 text-xs text-slate-400">In progress</p>
        </div>

        <div className="rounded-2xl border border-strivre-lightnavy bg-white p-5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Shortlisted
            </p>
            <Star size={18} className="text-violet-600" />
          </div>
          <p className="mt-2 text-3xl font-bold text-violet-600">
            {counts.Shortlisted}
          </p>
          <p className="mt-1 text-xs text-slate-400">Under consideration</p>
        </div>

        <div className="rounded-2xl border border-strivre-lightnavy bg-white p-5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Hired
            </p>
            <Trophy size={18} className="text-emerald-600" />
          </div>
          <p className="mt-2 text-3xl font-bold text-emerald-600">{counts.Hired}</p>
          <p className="mt-1 text-xs text-slate-400">Offers accepted</p>
        </div>

        <div className="rounded-2xl border border-strivre-lightnavy bg-white p-5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Rejected
            </p>
            <XCircle size={18} className="text-red-600" />
          </div>
          <p className="mt-2 text-3xl font-bold text-red-600">{counts.Rejected}</p>
          <p className="mt-1 text-xs text-slate-400">Not proceeding</p>
        </div>
      </div>

      {/* Candidate Pipeline */}
      <section className="rounded-2xl border border-strivre-lightnavy bg-white p-6 shadow-sm">
        <h3 className="mb-5 text-lg font-bold text-slate-800">Candidate Pipeline</h3>

        <div className="flex flex-col gap-3 md:flex-row md:items-stretch">
          {PIPELINE_STAGES.map((stage, index) => {
            const count = counts[stage];
            const pct = total ? Math.round((count / total) * 100) : 0;
            const active = filters.status === stage;

            return (
              <div key={stage} className="flex flex-1 items-center gap-3">
                <button
                  onClick={() => filterByStage(stage)}
                  className={`flex-1 rounded-xl border p-4 text-left transition ${
                    active
                      ? 'border-strivre-burgundy bg-strivre-lightburgundy'
                      : 'border-slate-100 bg-slate-50 hover:border-strivre-lightnavy'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`h-2.5 w-2.5 rounded-full ${STATUS_DOT[stage]}`} />
                    <span className="text-sm font-semibold text-slate-700">{stage}</span>
                  </div>
                  <p className="mt-2 text-2xl font-bold text-slate-900">{count}</p>
                  <p className="text-xs text-slate-500">{pct}% of total</p>
                </button>

                {index < PIPELINE_STAGES.length - 1 && (
                  <ArrowRight size={18} className="hidden shrink-0 text-slate-300 md:block" />
                )}
              </div>
            );
          })}

          <div className="flex flex-1 items-center gap-3">
            <button
              onClick={() => filterByStage('Rejected')}
              className={`flex-1 rounded-xl border p-4 text-left transition ${
                filters.status === 'Rejected'
                  ? 'border-strivre-burgundy bg-strivre-lightburgundy'
                  : 'border-red-100 bg-red-50/50 hover:border-red-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-red-600" />
                <span className="text-sm font-semibold text-red-700">Rejected</span>
              </div>
              <p className="mt-2 text-2xl font-bold text-slate-900">
                {counts.Rejected}
              </p>
              <p className="text-xs text-slate-500">
                {total ? Math.round((counts.Rejected / total) * 100) : 0}% of total
              </p>
            </button>
          </div>
        </div>
      </section>

      {/* Search & Filters */}
      <section className="rounded-2xl border border-strivre-lightnavy bg-white p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              className="input-base pl-9"
              placeholder="Search name, email, or phone"
              value={filters.search}
              onChange={(e) =>
                setFilters((current) => ({ ...current, search: e.target.value }))
              }
            />
          </div>

          <select
            className="input-base"
            value={filters.status}
            onChange={(e) =>
              setFilters((current) => ({
                ...current,
                status: e.target.value as Filters['status'],
              }))
            }
          >
            <option value="All">All Statuses</option>
            {STATUS_ORDER.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>

          <select
            className="input-base"
            value={filters.jobId}
            onChange={(e) =>
              setFilters((current) => ({ ...current, jobId: e.target.value }))
            }
          >
            <option value="All">All Positions</option>
            {jobs.map((job) => (
              <option key={job.jobId} value={job.jobId}>
                {job.title}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">
              Applied From
            </label>
            <input
              type="date"
              className="input-base"
              value={filters.from}
              onChange={(e) =>
                setFilters((current) => ({ ...current, from: e.target.value }))
              }
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">
              Applied To
            </label>
            <input
              type="date"
              className="input-base"
              value={filters.to}
              onChange={(e) =>
                setFilters((current) => ({ ...current, to: e.target.value }))
              }
            />
          </div>

          <div className="flex items-end gap-2">
            <button
              onClick={clearFilters}
              disabled={!filtersActive}
              className="btn-outline btn-md flex-1 rounded-lg disabled:opacity-40"
            >
              Clear Filters
            </button>

            <button
              onClick={() => exportRows(filtered, 'applicants.csv')}
              disabled={!filtered.length}
              className="btn-primary btn-md inline-flex flex-1 items-center justify-center gap-2 rounded-lg disabled:opacity-40"
            >
              <Download size={15} />
              Export
            </button>
          </div>
        </div>
      </section>

      {/* Bulk Actions */}
      {selected.size > 0 && (
        <div className="flex flex-col gap-3 rounded-2xl border border-strivre-burgundy bg-strivre-lightburgundy p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-semibold text-strivre-navy">
            {selected.size} applicant{selected.size === 1 ? '' : 's'} selected
          </p>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => bulkSetStatus('Reviewed')}
              className="btn-outline btn-sm rounded-lg"
            >
              Move to Reviewed
            </button>
            <button
              onClick={() => bulkSetStatus('Shortlisted')}
              className="btn-outline btn-sm rounded-lg"
            >
              Shortlist
            </button>
            <button
              onClick={() => bulkSetStatus('Hired')}
              className="btn-outline btn-sm rounded-lg"
            >
              Hire
            </button>
            <button
              onClick={() => bulkSetStatus('Rejected')}
              className="btn-outline btn-sm rounded-lg"
            >
              Reject
            </button>
            <button
              onClick={() =>
                exportRows(
                  applicants.filter((app) => selected.has(app.applicantId)),
                  'selected-applicants.csv'
                )
              }
              className="btn-primary btn-sm inline-flex items-center gap-2 rounded-lg"
            >
              <Download size={14} />
              Export Selected
            </button>
          </div>
        </div>
      )}

      {/* Main Applicants Table */}
      <section className="overflow-hidden rounded-2xl border border-strivre-lightnavy bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-left text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-4">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                </th>
                <th className="px-3 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Applicant
                </th>
                <th className="px-3 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Position
                </th>
                <th className="px-3 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Email
                </th>
                <th className="px-3 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Contact
                </th>
                <th className="px-3 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Applied
                </th>
                <th className="px-3 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Status
                </th>
                <th className="px-3 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Resume
                </th>
                <th className="px-3 py-4 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {filtered.map((app) => {
                const resume = resumeHref(app);
                return (
                  <tr key={app.applicantId} className="hover:bg-slate-50">
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selected.has(app.applicantId)}
                        onChange={() => toggleSelected(app.applicantId)}
                        className="h-4 w-4 rounded border-slate-300"
                      />
                    </td>

                    <td className="px-3 py-4">
                      <button onClick={() => setActiveApplicant(app)} className="text-left">
                        <p className="font-semibold text-slate-900 hover:text-strivre-burgundy">
                          {label(app)}
                        </p>
                        <p className="mt-0.5 text-xs text-slate-500">{app.emailAddress}</p>
                        <p className="mt-0.5 text-xs text-slate-400">
                          Applied {formatDate(appliedOn(app))}
                        </p>
                      </button>
                    </td>

                    <td className="px-3 py-4 text-slate-600">{jobTitle(app.jobId)}</td>

                    <td className="px-3 py-4 text-slate-600">
                      <span className="flex items-center gap-1.5">
                        <Mail size={13} />
                        {app.emailAddress}
                      </span>
                    </td>

                    <td className="px-3 py-4 text-slate-600">
                      <span className="flex items-center gap-1.5">
                        <Phone size={13} />
                        {app.contactNumber ?? '-'}
                      </span>
                    </td>

                    <td className="px-3 py-4 text-slate-600">{formatDate(appliedOn(app))}</td>

                    <td className="px-3 py-4">
                      <span
                        className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${STATUS_BADGE[app.applicationStatus]}`}
                      >
                        {app.applicationStatus}
                      </span>
                    </td>

                    <td className="px-3 py-4">
                      {resume ? (
                        <div className="flex items-center gap-2">
                          <a
                            href={resume}
                            target="_blank"
                            rel="noreferrer"
                            title="View Resume"
                            className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:text-strivre-burgundy"
                          >
                            <Eye size={15} />
                          </a>
                          <a
                            href={resume}
                            download
                            title="Download Resume"
                            className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:text-strivre-burgundy"
                          >
                            <Download size={15} />
                          </a>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">Unavailable</span>
                      )}
                    </td>

                    <td className="px-3 py-4">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        <button
                          onClick={() => setActiveApplicant(app)}
                          title="View Profile"
                          className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:text-strivre-burgundy"
                        >
                          <UserRound size={15} />
                        </button>
                        <button
                          disabled={update.isPending}
                          onClick={() => setStatus(app.applicantId, app.jobId, 'Reviewed')}
                          title="Mark Reviewed"
                          className="rounded-lg border border-slate-200 p-2 text-blue-600 hover:bg-blue-50"
                        >
                          <Eye size={15} />
                        </button>
                        <button
                          disabled={update.isPending}
                          onClick={() => setStatus(app.applicantId, app.jobId, 'Shortlisted')}
                          title="Shortlist"
                          className="rounded-lg border border-slate-200 p-2 text-violet-600 hover:bg-violet-50"
                        >
                          <Star size={15} />
                        </button>
                        <button
                          disabled={update.isPending}
                          onClick={() => setStatus(app.applicantId, app.jobId, 'Hired')}
                          title="Hire"
                          className="rounded-lg border border-slate-200 p-2 text-emerald-600 hover:bg-emerald-50"
                        >
                          <ThumbsUp size={15} />
                        </button>
                        <button
                          disabled={update.isPending}
                          onClick={() => setStatus(app.applicantId, app.jobId, 'Rejected')}
                          title="Reject"
                          className="rounded-lg border border-slate-200 p-2 text-red-600 hover:bg-red-50"
                        >
                          <ThumbsDown size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {!applicants.length && (
          <div className="p-16 text-center">
            <Users size={40} className="mx-auto text-slate-300" />
            <h3 className="mt-4 text-lg font-semibold text-slate-800">
              No Applicants Yet
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              Applications will appear here as soon as candidates apply to your
              open positions.
            </p>
          </div>
        )}

        {applicants.length > 0 && !filtered.length && filtersActive && (
          <div className="p-16 text-center">
            <Search size={40} className="mx-auto text-slate-300" />
            <h3 className="mt-4 text-lg font-semibold text-slate-800">
              No Matching Results
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              No applicants match your search or filters. Try adjusting your
              criteria.
            </p>
            <button
              onClick={clearFilters}
              className="mt-4 text-sm font-semibold text-strivre-burgundy hover:underline"
            >
              Clear all filters
            </button>
          </div>
        )}
      </section>

      {/* Recent Activity Feed */}
      <section className="rounded-2xl border border-strivre-lightnavy bg-white p-6 shadow-sm">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-800">Recent Activity</h3>
          <span className="text-sm text-slate-500">
            {recentActivity.length} update{recentActivity.length === 1 ? '' : 's'}
          </span>
        </div>

        {recentActivity.length ? (
          <div className="space-y-1">
            {recentActivity.map((app) => (
              <div
                key={app.applicantId}
                className="flex items-center justify-between rounded-xl px-3 py-3 transition hover:bg-slate-50"
              >
                <div className="flex items-center gap-3">
                  <span className={`h-2.5 w-2.5 rounded-full ${STATUS_DOT[app.applicationStatus]}`} />
                  <div>
                    <p className="font-semibold text-slate-900">{label(app)}</p>
                    <p className="text-xs text-slate-500">
                      {app.applicationStatus === 'New'
                        ? 'Applied for'
                        : `Status updated to ${app.applicationStatus} for`}{' '}
                      {jobTitle(app.jobId)}
                    </p>
                  </div>
                </div>
                <span className="text-xs text-slate-400">
                  {formatDateTime(appliedOn(app))}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-slate-500">
            No recent activity to show.
          </p>
        )}
      </section>

      {/* Applicant Details Drawer */}
      {activeApplicant && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40">
          <div className="absolute inset-0" onClick={() => setActiveApplicant(null)} />

          <div className="relative flex h-full w-full max-w-md flex-col overflow-y-auto border-l border-strivre-lightnavy bg-white p-6 shadow-xl">
            <div className="mb-6 flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-strivre-burgundy">
                  Applicant Profile
                </p>
                <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-strivre-navy">
                  <UserRound size={20} />
                  {label(activeApplicant)}
                </h2>
              </div>

              <button
                onClick={() => setActiveApplicant(null)}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-6">
              <section>
                <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-400">
                  Profile Information
                </h3>
                <div className="space-y-2 rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm">
                  <p className="flex items-center gap-2 text-slate-700">
                    <UserRound size={14} /> {label(activeApplicant)}
                  </p>
                  <p className="flex items-center gap-2 text-slate-700">
                    <Mail size={14} /> {activeApplicant.emailAddress}
                  </p>
                  <p className="flex items-center gap-2 text-slate-700">
                    <Phone size={14} /> {activeApplicant.contactNumber ?? 'Not provided'}
                  </p>
                </div>
              </section>

              <section>
                <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-400">
                  Application Information
                </h3>
                <div className="space-y-2 rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm">
                  <p className="flex items-center justify-between">
                    <span className="text-slate-500">Job Applied For</span>
                    <span className="font-semibold text-slate-800">
                      {jobTitle(activeApplicant.jobId)}
                    </span>
                  </p>
                  <p className="flex items-center justify-between">
                    <span className="text-slate-500">Applied Date</span>
                    <span className="font-semibold text-slate-800">
                      {formatDate(appliedOn(activeApplicant))}
                    </span>
                  </p>
                  <p className="flex items-center justify-between">
                    <span className="text-slate-500">Current Status</span>
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_BADGE[activeApplicant.applicationStatus]}`}
                    >
                      {activeApplicant.applicationStatus}
                    </span>
                  </p>
                </div>
              </section>

              <section>
                <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-400">
                  Documents
                </h3>
                {resumeHref(activeApplicant) ? (
                  <a
                    href={resumeHref(activeApplicant)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-lg bg-strivre-lightburgundy px-4 py-2.5 text-sm font-semibold text-strivre-burgundy hover:opacity-80"
                  >
                    <Download size={15} />
                    Download Resume
                  </a>
                ) : (
                  <p className="text-sm text-slate-500">No resume available.</p>
                )}
              </section>

              {activeApplicant.coverLetter && (
                <section>
                  <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-400">
                    Cover Letter
                  </h3>
                  <p className="whitespace-pre-wrap rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm leading-6 text-slate-600">
                    {activeApplicant.coverLetter}
                  </p>
                </section>
              )}

              <section>
                <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-400">
                  Recruiter Notes
                </h3>
                <textarea
                  rows={3}
                  placeholder="Add internal comments about this candidate (coming soon)"
                  disabled
                  className="input-base resize-none disabled:cursor-not-allowed"
                />
              </section>

              <section>
                <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-400">
                  Activity Timeline
                </h3>
                <div className="space-y-3">
                  {[
                    {
                      icon: Calendar,
                      label: 'Applied',
                      done: true,
                      date: appliedOn(activeApplicant),
                    },
                    {
                      icon: Eye,
                      label: 'Reviewed',
                      done:
                        STATUS_ORDER.indexOf(activeApplicant.applicationStatus) >=
                        STATUS_ORDER.indexOf('Reviewed'),
                    },
                    {
                      icon: Star,
                      label: 'Shortlisted',
                      done:
                        activeApplicant.applicationStatus === 'Shortlisted' ||
                        activeApplicant.applicationStatus === 'Hired',
                    },
                    {
                      icon:
                        activeApplicant.applicationStatus === 'Rejected'
                          ? XCircle
                          : CheckCircle2,
                      label:
                        activeApplicant.applicationStatus === 'Rejected'
                          ? 'Rejected'
                          : 'Hired',
                      done:
                        activeApplicant.applicationStatus === 'Rejected' ||
                        activeApplicant.applicationStatus === 'Hired',
                    },
                  ].map((step, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <span
                        className={`flex h-7 w-7 items-center justify-center rounded-full ${
                          step.done
                            ? 'bg-strivre-burgundy text-white'
                            : 'bg-slate-100 text-slate-400'
                        }`}
                      >
                        <step.icon size={14} />
                      </span>
                      <div className="flex flex-1 items-center justify-between">
                        <span
                          className={`text-sm font-medium ${
                            step.done ? 'text-slate-800' : 'text-slate-400'
                          }`}
                        >
                          {step.label}
                        </span>
                        {step.date && (
                          <span className="text-xs text-slate-400">
                            {formatDate(step.date)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="flex flex-wrap gap-2 border-t border-slate-100 pt-5">
                <button
                  onClick={() =>
                    setStatus(activeApplicant.applicantId, activeApplicant.jobId, 'Reviewed')
                  }
                  className="btn-outline btn-sm rounded-lg"
                >
                  Mark Reviewed
                </button>
                <button
                  onClick={() =>
                    setStatus(activeApplicant.applicantId, activeApplicant.jobId, 'Shortlisted')
                  }
                  className="btn-outline btn-sm rounded-lg"
                >
                  Shortlist
                </button>
                <button
                  onClick={() =>
                    setStatus(activeApplicant.applicantId, activeApplicant.jobId, 'Hired')
                  }
                  className="btn-primary btn-sm rounded-lg"
                >
                  Hire
                </button>
                <button
                  onClick={() =>
                    setStatus(activeApplicant.applicantId, activeApplicant.jobId, 'Rejected')
                  }
                  className="btn-outline btn-sm rounded-lg text-red-600"
                >
                  Reject
                </button>
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicantsOverview;